"use client";

// useSheetLeave — the way down, for a sheet its caller mounts conditionally.
//
// EtherModal plays its exit (260ms) only while it is still mounted and `open`
// has turned false. A caller that renders `{thing && <Setup onClose=… />}`
// unmounts the whole tree the moment it closes, so the sheet would vanish
// instead of sinking. This hook lets the component own that beat: `leave()`
// turns `open` off, waits for the sheet to settle onto the bottom edge, then
// tells the caller it may unmount. The caller's own `onClose` is unchanged.
//
//   const { open, leave } = useSheetLeave(onClose);
//   <EtherModal open={open} onClose={leave} …>
//
// Every way out of the sheet (×, Esc, the veil, the pull, its own button) goes
// through `leave`; calling it twice is harmless. `leave(then)` tells the caller
// something other than `onClose` once the sheet is down (a flow that finished:
// "connected, refresh the list"). A button may hand `leave` straight to onClick:
// the click event it receives is not a function, so it is ignored.

import { useCallback, useEffect, useRef, useState } from "react";

/** a beat longer than EtherModal's 260ms exit, so the sheet is down before it unmounts */
const LEAVE_MS = 280;

export function useSheetLeave(onClose: () => void, ms: number = LEAVE_MS): { open: boolean; leave: (then?: unknown) => void } {
  const [open, setOpen] = useState(true);
  const timer = useRef<number | null>(null);
  const done = useRef(onClose);
  useEffect(() => {
    done.current = onClose;
  }, [onClose]);
  // unmounted by the caller for another reason: nothing is left to tell
  useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );
  const leave = useCallback(
    (then?: unknown) => {
      if (timer.current != null) return;
      setOpen(false);
      const after = typeof then === "function" ? (then as () => void) : null;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        (after ?? done.current)();
      }, ms);
    },
    [ms],
  );
  return { open, leave };
}
